import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuthStore } from "../store/authStore";

export default function LoginPage() {
  const [form, setForm] = useState({ username: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { setAuth } = useAuthStore();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login/", form);
      setAuth(data.user, data.access, data.refresh);
      navigate("/");
    } catch (err: any) {
      setError(
        err.response?.data?.non_field_errors?.[0] ||
          "Login failed. Check your credentials."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.root}>
      {/* ── Left panel ── */}
      <div style={styles.left}>
        <div style={styles.leftInner}>
          {/* Logo */}
          <div style={styles.logo}>
            <div style={styles.logoIcon}>
              <svg viewBox="0 0 24 24" style={{ width: 26, height: 26, fill: "white" }}>
                <path d="M12 2C6 8 4 12 4 15a8 8 0 0016 0c0-3-2-7-8-13z" />
              </svg>
            </div>
            <span style={styles.logoText}>Kisiwa Waters</span>
          </div>

          {/* Tagline */}
          <div style={styles.heroBlock}>
            <h1 style={styles.heroTitle}>
              Smart water<br />
              <span style={styles.heroAccent}>billing made easy.</span>
            </h1>
            <p style={styles.heroSub}>
              Monitor meters, automate billing, and collect M-Pesa payments — all from one powerful dashboard.
            </p>
          </div>

          {/* Floating stat cards */}
          <div style={styles.statsRow}>
            <div style={styles.statCard}>
              <span style={styles.statValue}>2,400+</span>
              <span style={styles.statLabel}>Active meters</span>
            </div>
            <div style={styles.statCard}>
              <span style={styles.statValue}>99.9%</span>
              <span style={styles.statLabel}>Uptime</span>
            </div>
            <div style={styles.statCard}>
              <span style={styles.statValue}>KES 4.2M</span>
              <span style={styles.statLabel}>Monthly volume</span>
            </div>
          </div>

          {/* Decorative circles */}
          <div style={{ ...styles.circle, width: 320, height: 320, top: -80, right: -80, opacity: 0.12 }} />
          <div style={{ ...styles.circle, width: 200, height: 200, bottom: 80, left: -60, opacity: 0.1 }} />
        </div>
      </div>

      {/* ── Right panel ── */}
      <div style={styles.right}>
        <div style={styles.formCard}>
          {/* Header */}
          <div style={styles.formHeader}>
            <div style={styles.formIconWrap}>
              <svg viewBox="0 0 24 24" style={{ width: 22, height: 22, fill: "#0ea5e9" }}>
                <path d="M12 2C6 8 4 12 4 15a8 8 0 0016 0c0-3-2-7-8-13z" />
              </svg>
            </div>
            <h2 style={styles.formTitle}>Welcome back</h2>
            <p style={styles.formSub}>Sign in to your Kisiwa Waters account</p>
          </div>

          {/* Error */}
          {error && (
            <div style={styles.errorBox}>
              <svg viewBox="0 0 24 24" style={{ width: 16, height: 16, fill: "#dc2626", flexShrink: 0 }}>
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
              </svg>
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Username</label>
              <div style={styles.inputWrap}>
                <svg viewBox="0 0 24 24" style={styles.inputIcon}>
                  <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
                </svg>
                <input
                  type="text"
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  style={styles.input}
                  placeholder="Enter your username"
                  required
                />
              </div>
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Password</label>
              <div style={styles.inputWrap}>
                <svg viewBox="0 0 24 24" style={styles.inputIcon}>
                  <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
                </svg>
                <input
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  style={{ ...styles.input, paddingRight: 44 }}
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  style={styles.eyeBtn}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg viewBox="0 0 24 24" style={{ width: 18, height: 18, fill: "#94a3b8" }}>
                      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" style={{ width: 18, height: 18, fill: "#94a3b8" }}>
                      <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading} style={loading ? styles.btnDisabled : styles.btn}>
              {loading ? (
                <span style={styles.btnInner}>
                  <span style={styles.spinner} /> Signing in…
                </span>
              ) : (
                <span style={styles.btnInner}>
                  Sign In
                  <svg viewBox="0 0 24 24" style={{ width: 18, height: 18, fill: "white" }}>
                    <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
                  </svg>
                </span>
              )}
            </button>
          </form>

          <p style={styles.switchText}>
            Don't have an account?{" "}
            <Link to="/register" style={styles.switchLink}>
              Create one free →
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

/* ── Styles ──────────────────────────────────────────────────────────── */
const grad = "linear-gradient(135deg, #0ea5e9 0%, #14b8a6 100%)";

const styles: Record<string, React.CSSProperties> = {
  root: {
    display: "flex",
    minHeight: "100vh",
    fontFamily: "'Afacad Flux', sans-serif",
  },
  /* LEFT */
  left: {
    flex: "0 0 48%",
    background: "linear-gradient(145deg, #0f172a 0%, #0c4a6e 60%, #0e7490 100%)",
    position: "relative",
    overflow: "hidden",
    display: "flex",
    alignItems: "stretch",
  },
  leftInner: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    padding: "48px 52px",
    width: "100%",
  },
  logo: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  logoIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    background: grad,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 4px 16px rgba(14,165,233,.4)",
  },
  logoText: {
    fontWeight: 800,
    fontSize: "1.25rem",
    color: "white",
    letterSpacing: "-0.02em",
  },
  heroBlock: {
    marginTop: 0,
  },
  heroTitle: {
    fontSize: "clamp(2rem, 3.5vw, 3rem)",
    fontWeight: 900,
    color: "white",
    lineHeight: 1.1,
    letterSpacing: "-0.03em",
    margin: 0,
    marginBottom: 20,
  },
  heroAccent: {
    background: grad,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  },
  heroSub: {
    fontSize: "1rem",
    color: "rgba(186,230,253,0.85)",
    lineHeight: 1.7,
    maxWidth: 360,
    margin: 0,
  },
  statsRow: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
  },
  statCard: {
    background: "rgba(255,255,255,0.08)",
    backdropFilter: "blur(12px)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 14,
    padding: "14px 20px",
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  statValue: {
    fontWeight: 800,
    fontSize: "1.25rem",
    color: "white",
    letterSpacing: "-0.02em",
  },
  statLabel: {
    fontSize: "0.75rem",
    color: "rgba(186,230,253,0.7)",
    fontWeight: 500,
  },
  circle: {
    position: "absolute",
    borderRadius: "50%",
    background: "radial-gradient(circle, #0ea5e9 0%, transparent 70%)",
    pointerEvents: "none",
  },

  /* RIGHT */
  right: {
    flex: 1,
    background: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "32px 24px",
  },
  formCard: {
    width: "100%",
    maxWidth: 440,
    background: "white",
    borderRadius: 24,
    boxShadow: "0 20px 60px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.04)",
    padding: "44px 40px",
    border: "1px solid #e2e8f0",
  },
  formHeader: {
    textAlign: "center",
    marginBottom: 32,
  },
  formIconWrap: {
    width: 56,
    height: 56,
    borderRadius: 16,
    background: "#e0f2fe",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 16px",
  },
  formTitle: {
    fontWeight: 800,
    fontSize: "1.7rem",
    color: "#0f172a",
    margin: 0,
    marginBottom: 6,
    letterSpacing: "-0.02em",
  },
  formSub: {
    fontSize: "0.9rem",
    color: "#64748b",
    margin: 0,
  },
  errorBox: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "#fef2f2",
    border: "1px solid #fecaca",
    borderRadius: 10,
    padding: "10px 14px",
    fontSize: "0.85rem",
    color: "#dc2626",
    marginBottom: 20,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 20,
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  label: {
    fontSize: "0.85rem",
    fontWeight: 600,
    color: "#374151",
  },
  inputWrap: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  inputIcon: {
    position: "absolute",
    left: 14,
    width: 18,
    height: 18,
    fill: "#94a3b8",
    pointerEvents: "none",
    flexShrink: 0,
  } as React.CSSProperties,
  input: {
    width: "100%",
    border: "1.5px solid #e2e8f0",
    borderRadius: 12,
    padding: "12px 14px 12px 44px",
    fontSize: "0.95rem",
    fontFamily: "'Afacad Flux', sans-serif",
    color: "#0f172a",
    outline: "none",
    background: "#f8fafc",
    transition: "border-color 0.2s, box-shadow 0.2s",
    boxSizing: "border-box",
  } as React.CSSProperties,
  eyeBtn: {
    position: "absolute",
    right: 12,
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 4,
    display: "flex",
    alignItems: "center",
  } as React.CSSProperties,
  btn: {
    marginTop: 4,
    padding: "13px",
    borderRadius: 12,
    background: grad,
    border: "none",
    color: "white",
    fontFamily: "'Afacad Flux', sans-serif",
    fontSize: "1rem",
    fontWeight: 700,
    cursor: "pointer",
    boxShadow: "0 4px 16px rgba(14,165,233,0.4)",
    transition: "opacity 0.2s, transform 0.15s",
    width: "100%",
  },
  btnDisabled: {
    marginTop: 4,
    padding: "13px",
    borderRadius: 12,
    background: "linear-gradient(135deg, #7dd3fc 0%, #5eead4 100%)",
    border: "none",
    color: "white",
    fontFamily: "'Afacad Flux', sans-serif",
    fontSize: "1rem",
    fontWeight: 700,
    cursor: "not-allowed",
    width: "100%",
  },
  btnInner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  } as React.CSSProperties,
  spinner: {
    display: "inline-block",
    width: 16,
    height: 16,
    border: "2px solid rgba(255,255,255,0.4)",
    borderTopColor: "white",
    borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
  } as React.CSSProperties,
  switchText: {
    textAlign: "center",
    fontSize: "0.875rem",
    color: "#64748b",
    marginTop: 24,
    marginBottom: 0,
  },
  switchLink: {
    color: "#0ea5e9",
    fontWeight: 700,
    textDecoration: "none",
  },
};
